import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Star, MapPin } from 'lucide-react';
import { useSiteSeo } from '@/lib/useSiteSeo';
import { usePageMedia } from '@/lib/usePageMedia';
import LocalBusinessSchema from '@/components/seo/LocalBusinessSchema';
import NewsletterSignup from '@/components/NewsletterSignup';
import HomeReviews from '@/components/HomeReviews';
import TripAdvisorSummary from '@/components/TripAdvisorSummary';
import FeaturedShowcaseRigs from '@/components/showcase/FeaturedShowcaseRigs';
import InstagramGallery from '@/components/InstagramGallery';
import CompetitionStrip from '@/components/home/CompetitionStrip';
import GroupBookingPromo from '@/components/home/GroupBookingPromo';
import { useOpeningHours } from '@/hooks/useOpeningHours';

const PRODUCT_CATEGORIES = [
  {
    title: 'SIM RIG PCS',
    desc: 'High-performance custom PCs built for the most demanding racing sims.',
    slot: 'category_sim_pcs',
    path: '/shop?category=gaming_computers',
  },
  {
    title: 'SIM RIG BRACKETS',
    desc: 'Precision-engineered mounting solutions for rock-solid sim racing setups.',
    slot: 'category_brackets',
    path: '/shop?category=accessories',
  },
  {
    title: 'SIM RIG BOOKINGS',
    desc: 'Experience the thrill first-hand with a session on our professional simulators.',
    slot: 'category_bookings',
    path: '/bookings',
  },
  {
    title: 'GIFT CARDS',
    desc: 'Give the gift of speed. Perfect for birthdays, Christmas, or any occasion.',
    slot: 'category_gift_cards',
    path: '/gift-cards',
  },
];

const MR_DIFFERENCE = [
  { title: 'HIGH PERFORMANCE', desc: 'Every component hand-selected for maximum performance and reliability.' },
  { title: 'BESPOKE BUILDS', desc: 'Each rig tailor-made to your exact specifications, needs and budget.' },
  { title: 'NEXT DAY DELIVERY', desc: 'Order before 1pm Mon–Thu for next day delivery to UK mainland addresses.' },
  { title: 'EXPERT SUPPORT', desc: 'Dedicated support from our team of passionate sim racing enthusiasts.' },
  { title: 'QUALITY GUARANTEED', desc: 'Premium materials and rigorous testing on every single build.' },
  { title: 'HIGHLY RATED', desc: 'Trusted by customers across the UK for outstanding quality and service.' },
];

const ABOUT_POINTS = [
  'Born from a genuine passion for motor racing',
  'Premium, high-specification components in every build',
  'Tailor-made to your individual needs & preferences',
  'Professional build service for home or workplace',
  'Full range of accessories to elevate your setup',
  'UK based — Unit 9, Croft Business Park, Bromborough',
];

const STATS = [
  { value: '100+', label: 'RIGS BUILT' },
  { value: '5.0', label: 'CUSTOMER RATED', star: true },
  { value: 'UK', label: 'BUILT & DELIVERED', pin: true },
];

export default function Home() {
  const { get: media } = usePageMedia();
  const { storefront, formatHours, isOpenNow } = useOpeningHours();
  useSiteSeo('home', {
    title: 'Custom Sim Racing Rigs, Motion Platforms & Simulator Experiences',
    description: 'Motion Reality builds custom sim racing rigs, motion platforms, wheels, pedals & brackets in the UK, and hosts bookable sim racing experiences in Bromborough, Liverpool. Next day delivery on hardware orders before 1pm Mon–Thu.',
    image: 'https://media.base44.com/images/public/69e75b1fdf3eee5e6d1453dc/de63d0e06_generated_66d7d18e.png',
  });

  return (
    <div className="bg-[#0d0d0f]">
      <LocalBusinessSchema />

      {/* ── DUAL-PATH HERO ── */}
      <section className="px-6 lg:px-10 pt-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 min-h-[44vh] md:h-[58vh]">
        {/* 01 — SHOP */}
        <Link to="/shop" className="group relative overflow-hidden min-h-[44vh] md:h-full">
          <img
            src={media('home', 'hero')}
            alt="Custom sim racing rig build"
            width={1920}
            height={1080}
            fetchpriority="high"
            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0f] via-[#0d0d0f]/55 to-[#0d0d0f]/20" />
          <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-10">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-primary text-[11px] font-bold tracking-[0.3em] mb-2">01 — SHOP</p>
              <h2 className="text-white font-black uppercase leading-[0.9] mb-3" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>
                SHOP THE<br />HARDWARE
              </h2>
              <p className="text-white/60 text-xs max-w-xs mb-4 leading-relaxed">
                Custom sim rigs, wheels, pedals &amp; accessories. Built to your spec, delivered to your door.
              </p>
              <span className="inline-flex items-center gap-2 bg-primary text-white font-bold text-xs tracking-widest px-5 py-2.5 group-hover:bg-primary/90 transition-colors">
                EXPLORE PRODUCTS <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </motion.div>
          </div>
        </Link>

        {/* 02 — EXPERIENCE */}
        <Link to="/bookings" className="group relative overflow-hidden min-h-[44vh] md:h-full">
          <img
            src={media('home', 'category_bookings')}
            alt="Book a sim racing experience"
            width={1920}
            height={1080}
            fetchpriority="high"
            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0f] via-[#0d0d0f]/55 to-[#0d0d0f]/20" />
          <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-10">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
              <p className="text-primary text-[11px] font-bold tracking-[0.3em] mb-2">02 — EXPERIENCE</p>
              <h2 className="text-white font-black uppercase leading-[0.9] mb-3" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>
                BOOK THE<br />EXPERIENCE
              </h2>
              <p className="text-white/60 text-xs max-w-xs mb-4 leading-relaxed">
                Adult &amp; junior sim racing sessions at our Bromborough facility.
              </p>
              <span className="inline-flex items-center gap-2 bg-primary text-white font-bold text-xs tracking-widest px-5 py-2.5 group-hover:bg-primary/90 transition-colors">
                BOOKINGS <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </motion.div>
          </div>
        </Link>
        </div>
      </section>

      {/* ── OUR PRODUCTS ── */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">WHAT WE OFFER</p>
            <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight">OUR PRODUCTS</h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {PRODUCT_CATEGORIES.map((cat, i) => (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Link to={cat.path} className="group block relative overflow-hidden bg-[#1a1a1f]">
                  <div className="aspect-[4/3] overflow-hidden bg-[#0d0d0f]">
                    <img
                      src={media('home', cat.slot)}
                      alt={cat.title}
                      width={400}
                      height={300}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold text-xs sm:text-sm tracking-wide">{cat.title}</h3>
                    <p className="text-white/50 text-[11px] mt-1 leading-relaxed hidden sm:block">{cat.desc}</p>
                  </div>
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STORY / STATS BAND ── */}
      <section className="py-16 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">OUR STORY</p>
            <h2 className="text-white font-black text-3xl md:text-4xl uppercase leading-tight mb-5">
              BUILT BY RACERS,<br />FOR RACERS.
            </h2>
            <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-md">
              Motion Reality was born from a personal passion for motor racing and a drive to enhance skills through experience across a wide range of circuits and conditions.
            </p>
            <div className="flex items-center gap-6 mb-7">
              {STATS.map((s, i) => (
                <React.Fragment key={s.label}>
                  {i > 0 && <div className="w-px h-9 bg-white/10" />}
                  <div>
                    <p className="text-white font-black text-2xl leading-none flex items-center gap-1">
                      {s.star && <Star className="w-4 h-4 text-primary fill-primary" />}
                      {s.pin && <MapPin className="w-4 h-4 text-primary" />}
                      {s.value}
                    </p>
                    <p className="text-white/40 text-[10px] tracking-widest mt-1">{s.label}</p>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <Link to="/about" className="inline-flex items-center gap-2 text-primary font-bold text-xs tracking-widest hover:text-primary/80 transition-colors">
              OUR STORY <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ABOUT_POINTS.map((point) => (
              <div key={point} className="flex items-start gap-2.5 border border-white/5 bg-[#111115] p-3.5">
                <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <p className="text-white/60 text-xs leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MOTION REALITY DIFFERENCE ── */}
      <section className="py-16 px-6 lg:px-10 bg-[#111115] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">WHY MOTION REALITY</p>
            <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight">THE MOTION REALITY DIFFERENCE</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5 border border-white/5">
            {MR_DIFFERENCE.map((item) => (
              <div key={item.title} className="bg-[#111115] p-5 hover:bg-[#1a1a1f] transition-colors duration-300">
                <div className="w-8 h-[2px] bg-primary mb-4" />
                <h3 className="text-white font-bold text-xs tracking-wide mb-2">{item.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT DRIVERS SAY (condensed reviews) ── */}
      <HomeReviews />

      {/* ── GROUP BOOKING PROMO (Corporate + Parties) ── */}
      <GroupBookingPromo />

      {/* ── TRIPADVISOR SUMMARY ── */}
      <TripAdvisorSummary />

      {/* ── FEATURED SIMULATORS ── */}
      <FeaturedShowcaseRigs />

      {/* ── COMPETITION PROMO ── */}
      <CompetitionStrip />

      {/* ── INSTAGRAM UGC ── */}
      <InstagramGallery />

      {/* ── PROMO SPLIT ── */}
      <section className="py-16 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/shop" className="group relative overflow-hidden bg-[#1a1a1f] aspect-[16/10] border border-white/5 hover:border-white/15 transition-colors">
            <img
              src={media('home', 'promo_shop')}
              alt="Shop sim racing hardware"
              width={800}
              height={500}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p className="text-primary text-xs font-bold tracking-widest mb-2">PRO-SPEC GEAR</p>
              <h3 className="text-white font-black text-xl uppercase mb-2">Shop The Store</h3>
              <p className="text-white/60 text-sm mb-4 max-w-xs">Sim rigs, wheels, pedals &amp; accessories. Next day UK delivery on orders before 1pm Mon–Thu.</p>
              <span className="inline-flex items-center gap-2 text-white text-sm font-bold group-hover:text-primary transition-colors">EXPLORE PRODUCTS <ArrowRight className="w-4 h-4" /></span>
            </div>
          </Link>
          <Link to="/bookings" className="group relative overflow-hidden bg-[#1a1a1f] aspect-[16/10] border border-white/5 hover:border-white/15 transition-colors">
            <img
              src={media('home', 'promo_bookings')}
              alt="Book a sim racing session"
              width={800}
              height={500}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p className="text-primary text-xs font-bold tracking-widest mb-2">GET BEHIND THE WHEEL</p>
              <h3 className="text-white font-black text-xl uppercase mb-2">Book A Session</h3>
              <p className="text-white/60 text-sm mb-4 max-w-xs">Adult &amp; junior sim racing experiences at our Bromborough facility.</p>
              <span className="inline-flex items-center gap-2 text-white text-sm font-bold group-hover:text-primary transition-colors">BOOK NOW <ArrowRight className="w-4 h-4" /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* ── BUILD / CONTACT CTA ── */}
      <section className="py-16 px-6 lg:px-10 bg-[#111115] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <p className="text-primary text-xs font-bold tracking-widest mb-2">03 — BUILD</p>
            <h2 className="text-white font-black text-2xl md:text-4xl uppercase leading-tight">
              READY TO BUILD<br />YOUR RIG?
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/shop" className="group block">
              <div className="w-full border border-primary/40 hover:border-primary bg-primary/5 hover:bg-primary text-white p-6 text-center transition-colors min-h-[130px] flex flex-col items-center justify-center gap-1.5">
                <p className="font-bold text-sm tracking-wide">VISIT OUR SHOP</p>
                <p className="text-white/70 text-xs">Browse the full range</p>
              </div>
            </Link>
            <a href="tel:01515283001" className="group block">
              <div className="w-full border border-primary/40 hover:border-primary bg-primary/5 hover:bg-primary p-6 text-center transition-colors min-h-[130px] flex flex-col items-center justify-center gap-1.5">
                <p className="text-white font-bold text-sm tracking-wide">CALL US</p>
                <p className="text-primary group-hover:text-white text-base font-bold transition-colors">0151 528 3001</p>
                <p className="text-white/30 group-hover:text-white/80 text-xs transition-colors">{storefront ? formatHours(storefront) : 'Mon–Sat 9am–6pm'}</p>
                {storefront && (
                  <span className={`inline-flex items-center gap-1.5 text-[9px] font-bold tracking-widest mt-1 ${isOpenNow(storefront) ? 'text-green-400' : 'text-white/30'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${isOpenNow(storefront) ? 'bg-green-400' : 'bg-white/30'}`} />
                    {isOpenNow(storefront) ? 'OPEN NOW' : 'CLOSED NOW'}
                  </span>
                )}
              </div>
            </a>
            <a href="mailto:sales@motionreality.co.uk" className="group block">
              <div className="w-full border border-primary/40 hover:border-primary bg-primary/5 hover:bg-primary p-6 text-center transition-colors min-h-[130px] flex flex-col items-center justify-center gap-1.5">
                <p className="text-white font-bold text-sm tracking-wide">EMAIL US</p>
                <p className="text-primary group-hover:text-white text-sm font-bold transition-colors">sales@motionreality.co.uk</p>
                <p className="text-white/30 group-hover:text-white/80 text-xs transition-colors">We respond within 24 hours</p>
              </div>
            </a>
          </div>

          <div className="mt-8 flex items-center gap-2 text-white/30 text-sm">
            <MapPin className="w-4 h-4 text-primary" />
            <span>Unit 9, Croft Business Park, Carrock Road, Bromborough, CH62 3RA</span>
          </div>
        </div>
      </section>

      {/* ── NEWSLETTER ── */}
      <section className="py-16 px-6 lg:px-10 bg-[#0d0d0f] border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">STAY IN THE LOOP</p>
          <h2 className="text-white font-black text-2xl md:text-4xl uppercase mb-3">Never Miss A Launch</h2>
          <p className="text-white/40 text-sm mb-8 max-w-md mx-auto">Get new product releases, sim racing experience offers and exclusive deals delivered to your inbox.</p>
          <div className="max-w-md mx-auto">
            <NewsletterSignup variant="section" />
          </div>
        </div>
      </section>

    </div>
  );
}